import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardActions,
    CardContent,
    CardMedia,
    CircularProgress,
    IconButton,
    Typography
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import WarningIcon from '@mui/icons-material/WarningAmberOutlined';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import moment from 'moment';
import OverlapModal from '../OverlapModal';
import ReactGA from 'react-ga4';
import { overlapBehavior, overlapType } from '../../helpers/overlap';
import { RemoteControlContext } from '../../ContextProvider';

const overlapMessage = (overlapType) => {
    if (overlapType === 'same') return 'This event conflicts with another event starting simultaneously. Unsubscribe from the initial event to play.';
    if (overlapType === 'after') return 'This event overlaps with an existing event and will play upon completion of existing event.';

    return 'This event conflicts with another added live event.';
};

export default function EventCard ({ event, futureEvent, liveNowEvent, otherEvents = [], handleEventSubClick, handleOverlapIconClick }) {
    const { isLoading } = useContext(RemoteControlContext);

    const { thumbnail, name, schedule_start, hasSubscription, iHaveSubscribed } = event;
    const [isTakingAction, setIsTakingAction] = useState(false);
    const [dialogIsOpen, setDialogIsOpen] = useState(false);
    const eventOverlapBehavior = () => !event.subscribed && overlapBehavior(event, otherEvents);
    const isOverlapped = event.subscribed && overlapType(event, otherEvents);

    useEffect(() => {
        if (!isLoading) {
            setIsTakingAction(false);
        }
    }, [isLoading]);

    const addText = futureEvent ? 'Add to List' : 'Play Now';
    const action = event.subscribed ? 'Remove Event' : addText;
    const addTag = futureEvent
        ? <ControlPointIcon fontSize='small' />
        : <PlayCircleOutlineIcon fontSize='small' />;

    const clickAction = () => {
        if (isLoading) return null;

        if (eventOverlapBehavior()) {
            return setDialogIsOpen(true);
        }

        setIsTakingAction(true);
        handleEventSubClick(event);
        ReactGA.event({
            category: action,
            action
        });
    };

    const setActions = () => {
        if (isTakingAction && isLoading) return <CircularProgress color='success' size={16} />;

        return (
            <>
                {event.subscribed
                    ? <DoneIcon color='success' fontSize='small' />
                    : addTag}
                <Typography variant='live-event-card-actions-caption'>{action}</Typography>
            </>
        );
    };

    return (
        <>
            <OverlapModal
              dialogOkAction={() => {
                    handleEventSubClick(event);
                    setDialogIsOpen(false);
                }}
              dialogIsOpen={dialogIsOpen}
              closeDialog={() => setDialogIsOpen(false)}
              eventOverlap={isOverlapped}
              overlap={eventOverlapBehavior()}
            />
            <div>

                <Card variant='live-event' onClick={clickAction}>
                    <Box position='relative'>
                        <CardMedia component='img' image={thumbnail} alt={name} />
                        {!!isOverlapped && (
                        <IconButton variant='event_overlap' onClick={(e) => handleOverlapIconClick(e, overlapMessage(isOverlapped))}>
                            <WarningIcon color='primary' fontSize='small' />
                        </IconButton>)}
                        {(schedule_start && !liveNowEvent) && (
                        <Typography variant='live-event-schedule'>
                            {moment(schedule_start).format('h:mm a | MMM D, YYYY')}
                        </Typography>)}
                        {liveNowEvent && (
                        <Typography variant='live-now-event'>
                            LIVE
                        </Typography>)}
                        {(hasSubscription && !iHaveSubscribed) && (
                            <Typography variant='live-now-parent'>
                                BY PARENT
                            </Typography>)}
                    </Box>
                    <CardContent variant='live-event'>
                        <Box display='flex' width='100%' alignItems='center' justifyContent='space-between'>
                            <Typography variant='live-event-name'>{name}</Typography>
                            <CardActions variant='live-event-card-actions'>
                                {setActions()}
                            </CardActions>
                        </Box>
                    </CardContent>
                </Card>
            </div>
        </>
    );
}
